import { designOverlapVolumes, TriangleSoup } from '../engine/design-region-bounds';
import { calculateVolume, FaceData } from '../engine/helpers';
import { PartType } from '../models/enums';

/**
 * Volumes of the design and non-design regions as the optimizer sees them:
 * where a non-design part overlaps a design part, the shared volume is
 * non-design. So a design part's volume here is its own volume less what
 * non-design parts cover, and the non-design parts keep their whole volume.
 */

/** The fields of an engine part the volumes need; faces share one frame. */
export interface RegionVolumePart {
  type?: PartType;
  data: { id: string; faces: FaceData[] };
}

/** Volumes in mm³. */
export interface RegionVolumes {
  /** Part id -> volume; a design part's leaves out what non-design parts cover. */
  partVolumes: Record<string, number>;
  designVolume: number;
  nonDesignVolume: number;
  /** Design part id -> its volume inside non-design parts. */
  designOverlapVolumes: Record<string, number>;
  /** Design volume inside non-design parts, all design parts together. */
  overlapVolume: number;
}

let cached: { key: readonly unknown[]; volumes: RegionVolumes } | null = null;

function faceSoup(faces: readonly FaceData[]): TriangleSoup {
  let count = 0;
  for (const face of faces) count += face.indices.length;
  const soup = new Float32Array(count * 3);
  let offset = 0;
  for (const { vertices, indices } of faces) {
    for (let i = 0; i < indices.length; i++) {
      const at = indices[i] * 3;
      soup[offset++] = vertices[at];
      soup[offset++] = vertices[at + 1];
      soup[offset++] = vertices[at + 2];
    }
  }
  return soup;
}

/** Callers keep and edit what they get (the project stores partVolumes), never the cache. */
function copyOf(volumes: RegionVolumes): RegionVolumes {
  return {
    ...volumes,
    partVolumes: { ...volumes.partVolumes },
    designOverlapVolumes: { ...volumes.designOverlapVolumes }
  };
}

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * Region volumes of `parts`. The overlap is measured once per set of parts
 * and geometry (by face-array identity), so calling this on every save or
 * panel open costs nothing until a part changes type or is replaced.
 */
export function regionVolumes(parts: readonly RegionVolumePart[]): RegionVolumes {
  const design = parts.filter((p) => p.type === PartType.DESIGN);
  const nondesign = parts.filter((p) => p.type === PartType.NONDESIGN);
  const key = [...design, ...nondesign].flatMap((p) => [p.type, p.data.id, p.data.faces]);
  if (cached && sameKey(cached.key, key)) return copyOf(cached.volumes);

  const partVolumes: Record<string, number> = {};
  const overlaps: Record<string, number> = {};
  let designVolume = 0;
  let nonDesignVolume = 0;
  let overlapVolume = 0;

  for (const part of nondesign) {
    partVolumes[part.data.id] = calculateVolume(part.data.faces);
    nonDesignVolume += partVolumes[part.data.id];
  }
  const measured =
    design.length > 0 && nondesign.length > 0
      ? designOverlapVolumes(
          design.map((p) => faceSoup(p.data.faces)),
          nondesign.map((p) => faceSoup(p.data.faces))
        )
      : [];
  design.forEach((part, index) => {
    const whole = calculateVolume(part.data.faces);
    // An unmeasurable overlap counts as none: the whole part, as before.
    const overlap = Math.min(Math.max(measured[index] ?? 0, 0), whole);
    partVolumes[part.data.id] = whole - overlap;
    designVolume += whole - overlap;
    if (overlap > 0) overlaps[part.data.id] = overlap;
    overlapVolume += overlap;
  });

  const volumes: RegionVolumes = {
    partVolumes,
    designVolume,
    nonDesignVolume,
    designOverlapVolumes: overlaps,
    overlapVolume
  };
  cached = { key, volumes };
  return copyOf(volumes);
}
